import { useEffect, useState, type ReactNode } from 'react';
import { releasePreloader } from '@/lib/preloader';
import { useT } from '@/i18n';
import { linkApi, type AuthSession } from '@/lib/api/auth';
import { HttpError } from '@/lib/api/http';
import { AuthScreen, AuthStatusScreen, LinkConfirmationScreen } from './AuthScreen';
import { useAuth } from './AuthProvider';
import { BrandPreloader } from './BrandPreloader';

type LinkState = 'loading' | 'pending' | 'confirm' | 'blocked' | 'expired' | 'error';

function linkErrorState(error: unknown): LinkState {
  if (!(error instanceof HttpError)) return 'error';
  if (error.code === 'invalid_or_expired_link') return 'expired';
  if (error.code === 'telegram_confirmation_required') return 'pending';
  if (error.code === 'linking_unavailable' || error.code === 'identity_conflict') return 'blocked';
  return 'error';
}

function SignedInLinkGate({ token, session, children }: { token: string; session: AuthSession; children: ReactNode }) {
  const { t } = useT('auth');
  const { complete, clearIncomingLink } = useAuth();
  const [state, setState] = useState<LinkState>('loading');
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setState('loading');
    try {
      const status = await linkApi.status(token, session.csrf_token);
      if (!status.confirmed || status.eligibility === 'pending') setState('pending');
      else if (status.eligibility === 'allowed') setState('confirm');
      else setState('blocked');
    } catch (error) {
      setState(linkErrorState(error));
    }
  };

  useEffect(() => { void check(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'loading') return <BrandPreloader text={t('link.checking')} />;
  if (state === 'pending') return (
    <AuthStatusScreen
      title={t('link.waitTitle')}
      text={t('link.waitText')}
      primary={t('action.checkAgain')}
      onPrimary={() => void check()}
      secondary={t('action.cancel')}
      onSecondary={clearIncomingLink}
    />
  );
  if (state === 'confirm') return (
    <LinkConfirmationScreen
      busy={busy}
      onConfirm={async () => {
        setBusy(true);
        try {
          const next = await linkApi.confirm(token, session.csrf_token);
          clearIncomingLink();
          await complete(next);
        } catch (error) {
          setState(linkErrorState(error));
        } finally {
          setBusy(false);
        }
      }}
      onCancel={clearIncomingLink}
    />
  );
  if (state === 'blocked') return (
    <AuthStatusScreen
      title={t('blocked.title')}
      text={t('blocked.text')}
      primary={t('action.continueApp')}
      onPrimary={clearIncomingLink}
    />
  );
  if (state === 'expired') return (
    <AuthStatusScreen
      title={t('link.expiredTitle')}
      text={t('link.expiredText')}
      primary={t('action.continueApp')}
      onPrimary={clearIncomingLink}
    />
  );
  if (state === 'error') return (
    <AuthStatusScreen
      title={t('error.title')}
      text={t('error.service')}
      primary={t('action.tryAgain')}
      onPrimary={() => void check()}
      secondary={t('action.continueApp')}
      onSecondary={clearIncomingLink}
    />
  );
  return <>{children}</>;
}

export function AuthBoundary({ children }: { children: ReactNode }) {
  const { t } = useT('auth');
  const auth = useAuth();
  const checking = auth.state.status === 'checking';
  // the first-frame splash (index.html) stays until the session is known, then dissolves
  useEffect(() => { if (!checking) releasePreloader(); }, [checking]);
  if (auth.state.status === 'checking') return <BrandPreloader />;
  if (auth.state.status === 'error') {
    return (
      <AuthStatusScreen
        title={t(auth.state.kind === 'offline' ? 'offline.title' : 'error.title')}
        text={t(auth.state.kind === 'offline' ? 'offline.text' : 'error.service')}
        primary={t('action.tryAgain')}
        onPrimary={() => void auth.retry()}
      />
    );
  }
  if (auth.state.status === 'anonymous') {
    return <AuthScreen notice={auth.state.notice} onAuthenticated={auth.complete} />;
  }
  if (auth.incomingLinkToken) {
    return (
      <SignedInLinkGate token={auth.incomingLinkToken} session={auth.state.session}>
        {children}
      </SignedInLinkGate>
    );
  }
  return <>{children}</>;
}
